import React, { useEffect, useState } from "react";
import { api, type Me } from "./api";
import { AllergyBanner, MedicationAllergyTag, medicationAllergiesOf, type AllergyPanelData } from "./AllergyBanner";
import { MedStatusBadge } from "./MedStatusBadge";
import { easternToUtcMs, easternMinutes } from "../server/station/windows";
import { correctionStart } from "./correction";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { doseFormRules } from "./checks";
import type { MedStatus } from "../server/station/med-status";

/**
 * The medication pass (build plan §9.1 #6), the single-student "Give now"
 * screen (§9.1 #7), and entering from paper (§9.1 #11). Tablet-first (800 px
 * and up), large controls; nothing breaks at 375 px. The medication allergy
 * banner is on every administration screen (Decisions 2 #22). What a row
 * records is the program standard (MAR); the server refuses an incomplete one.
 */

export interface WindowDef { code: string; name: string; startTime: string; endTime: string; retired: boolean }
export interface AdminView {
  adminKey: string;
  medKey: string;
  windowCode: string;
  scheduledOn: string;
  disposition: string;
  reason: string;
  amountGiven: string;
  givenAtUtc: string;
  administeredByEmail: string;
  administeredByName: string;
  enteredByEmail: string;
  enteredAtUtc: string;
  countersigned: string;
  fromPaper: boolean;
  countBefore: string;
  countAfter: string;
  countVerifiedByEmail: string;
  prnReportedBy: string;
  correctsAdminKey: string;
  correctionReason: string;
  notes: string;
}
export interface PassMed {
  medKey: string;
  name: string;
  strength: string;
  dose: string;
  route: string;
  frequency: string;
  windows: string[];
  isPrn: boolean;
  prnInstructions: string;
  controlled: boolean;
  heldByStation: boolean;
  selfCarry: boolean;
  lastCount: string;
  status: MedStatus;
}
export interface DoseSlot { med: PassMed; windowCode: string; scheduledOn: string; recorded: AdminView | null; history: AdminView[] }
export interface StudentDoses {
  personKey: string;
  lastName: string;
  firstName: string;
  dateOfBirth: string;
  city: string;
  allergy: AllergyPanelData;
  due: DoseSlot[];
  prn: { med: PassMed; today: AdminView[]; history?: Record<string, AdminView[]> }[];
  held: { medKey: string; name: string; status: MedStatus }[];
  prnOnly: boolean;
}
export interface DoseOptions {
  windows: WindowDef[];
  allWindows: WindowDef[];
  reasons: string[];
  givers: { email: string; name: string; credential: string }[];
  me: { email: string; mayGive: boolean };
  canRecord: boolean;
}
interface PassView extends DoseOptions { date: string; window: WindowDef | null; city: string; cities: string[]; students: StudentDoses[] }

export const DISPOSITION_LABEL: Record<string, string> = {
  Given: "Given",
  Held: "Held",
  Refused: "Refused",
  PrnNotNeeded: "Not needed (as needed)",
  SelfAdministered: "Self-administered (supervised)",
  Departed: "Departed",
};

const clock = (iso: string) => {
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "" : new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }).format(t);
};
export const stamp = (iso: string) => {
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "" : new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(t);
};
const nowHHMM = () => {
  const m = easternMinutes(Date.now());
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};
export const windowName = (o: Pick<DoseOptions, "allWindows">, code: string) => {
  const w = o.allWindows.find((x) => x.code === code);
  return w ? `${w.name} (${w.startTime}–${w.endTime})` : code || "Any time";
};

// ---- entering from paper (a per-tab session toggle, §9.1 #11) -------------------------------------

const PAPER_KEY = "medadmin.enteringFromPaper";
function readPaper(): boolean {
  try {
    return sessionStorage.getItem(PAPER_KEY) === "1";
  } catch {
    return false;
  }
}
export function usePaperMode(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(readPaper);
  return [
    on,
    (v) => {
      try {
        if (v) sessionStorage.setItem(PAPER_KEY, "1");
        else sessionStorage.removeItem(PAPER_KEY);
      } catch {
        /* storage blocked: the toggle lasts for this page only */
      }
      setOn(v);
    },
  ];
}

export function PaperBanner({ on, set }: { on: boolean; set: (v: boolean) => void }): React.ReactElement {
  return on ? (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border-2 border-violet-500 bg-violet-50 px-4 py-2 text-sm text-violet-950" role="status">
      <span><span className="font-bold">Entering from paper.</span> Type the time written on the sheet for each dose; it is saved as written, with the time you entered it.</span>
      <button type="button" className="btn-secondary" onClick={() => set(false)}>Stop entering from paper</button>
    </div>
  ) : (
    <p className="mb-3 text-right text-sm"><button type="button" className="text-program underline" onClick={() => set(true)}>Entering from paper</button></p>
  );
}

// ---- one dose -------------------------------------------------------------------------------------

/** What's recorded for a slot, with the correction history one click away. */
export function Recorded({ a, history, o }: { a: AdminView; history: AdminView[]; o: DoseOptions }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ok = a.disposition === "Given" || a.disposition === "SelfAdministered";
  return (
    <div className={`rounded-md px-3 py-2 text-sm ${ok ? "bg-emerald-50 text-emerald-950" : "bg-slate-100 text-slate-900"}`}>
      <p>
        <span className="font-semibold">{DISPOSITION_LABEL[a.disposition] ?? a.disposition}</span>
        {a.amountGiven && ` · ${a.amountGiven}`} · {clock(a.givenAtUtc)} · {a.administeredByName || a.administeredByEmail}
        {a.reason && ` · ${a.reason}`}
        {a.prnReportedBy && ` · reported by ${a.prnReportedBy}`}
        {a.countVerifiedByEmail && ` · count ${a.countBefore} → ${a.countAfter}, verified by ${a.countVerifiedByEmail}`}
      </p>
      <p className="text-xs opacity-80">
        {a.fromPaper ? `From paper: given ${stamp(a.givenAtUtc)}, entered ${stamp(a.enteredAtUtc)}` : `Entered ${stamp(a.enteredAtUtc)}`}{a.enteredByEmail !== a.administeredByEmail ? ` by ${a.enteredByEmail}` : ""}
        {a.countersigned === "N" && <span className="ml-1 rounded bg-amber-200 px-1 font-medium text-amber-950">unverified: {a.administeredByName || a.administeredByEmail} to countersign</span>}
        {a.countersigned === "Y" && " · countersigned"}
        {history.length > 0 && <> · <button type="button" className="underline" onClick={() => setOpen(!open)}>corrected — see history</button></>}
      </p>
      {a.notes && <p className="whitespace-pre-wrap text-xs">{a.notes}</p>}
      {open && (
        <ol className="mt-1 list-decimal pl-5 text-xs">
          {history.map((h) => (
            <li key={h.adminKey}>{DISPOSITION_LABEL[h.disposition] ?? h.disposition}{h.amountGiven ? ` · ${h.amountGiven}` : ""} · {stamp(h.givenAtUtc)} · {h.administeredByName || h.administeredByEmail} (entered {stamp(h.enteredAtUtc)} by {h.enteredByEmail})</li>
          ))}
          <li>Corrected: {a.correctionReason}</li>
        </ol>
      )}
      <p className="sr-only">{windowName(o, a.windowCode)}</p>
    </div>
  );
}

/**
 * The per-dose controls. `slot` for a scheduled dose (window and day fixed);
 * none for an as-needed medication. `corrects` makes it a correction.
 */
export function DoseForm({
  personKey,
  med,
  slot,
  date,
  o,
  paper,
  corrects,
  initial,
  onSaved,
  onCancel,
}: {
  personKey: string;
  med: PassMed;
  slot?: { windowCode: string; scheduledOn: string };
  date: string;
  o: DoseOptions;
  paper: boolean;
  corrects?: AdminView;
  /** The disposition to start on (Missed meds: back-fill, refused, held, not needed). */
  initial?: string;
  onSaved: () => void;
  onCancel?: () => void;
}): React.ReactElement {
  const choices = med.isPrn ? ["Given", "PrnNotNeeded", "Refused"] : med.selfCarry ? ["SelfAdministered", "Given", "Held", "Refused"] : ["Given", "Held", "Refused", "SelfAdministered"];
  // A correction starts from the entry it corrects; the reason for it stays empty.
  const start = corrects ? correctionStart(corrects, o.reasons) : null;
  const [disposition, setDisposition] = useState(start && choices.includes(start.disposition) ? start.disposition : initial && choices.includes(initial) ? initial : choices[0]);
  const [late, setLate] = useState(start?.late ?? "");
  const [amount, setAmount] = useState(start?.amount || med.dose);
  const [time, setTime] = useState(start?.time ?? (paper ? "" : nowHHMM()));
  const [by, setBy] = useState(start?.by ?? (o.me.mayGive ? o.me.email : ""));
  const [reason, setReason] = useState(start?.reason ?? "");
  const [reasonText, setReasonText] = useState(start?.reasonText ?? "");
  const [reportedBy, setReportedBy] = useState(start?.reportedBy ?? "");
  const [before, setBefore] = useState(med.lastCount);
  const [after, setAfter] = useState("");
  const [vEmail, setVEmail] = useState("");
  const [vPin, setVPin] = useState("");
  const [notes, setNotes] = useState(start?.notes ?? "");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taken = disposition === "Given" || disposition === "SelfAdministered";
  const counted = med.controlled && med.heldByStation && taken;
  // Given after the window closed (not from paper): the server asks why.
  const win = slot && !med.isPrn ? o.allWindows.find((x) => x.code === slot.windowCode) : undefined;
  const givenMs = time ? easternToUtcMs(corrects?.scheduledOn || slot?.scheduledOn || date, time) : Date.now();
  const isLate = Boolean(win && taken && !paper && givenMs > easternToUtcMs(slot!.scheduledOn, win.endTime));
  const id = (k: string) => `d-${med.medKey}-${slot?.windowCode ?? "prn"}-${corrects?.adminKey ?? "new"}-${k}`;
  const { problems, check } = useProblems(
    doseFormRules({ correcting: Boolean(corrects), why, disposition, amount, time, paper, by, reason, reasonText, hasReasons: o.reasons.length > 0, isLate, late, reportedBy, counted, before, after, vEmail, vPin }, id)
  );
  const P = ({ k }: { k: string }) => <FieldProblem id={id(k)} problems={problems} />;
  const bad = (k: string) => invalid(id(k), problems);

  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const day = corrects?.scheduledOn || slot?.scheduledOn || date;
    const r = await api<{ adminKey: string }>("POST", `/api/pass/${encodeURIComponent(personKey)}/doses`, {
      medKey: med.medKey,
      // A correction keeps the entry's window and day (an as-needed entry: the ones it was recorded with).
      windowCode: corrects ? corrects.windowCode : slot?.windowCode ?? "",
      scheduledOn: corrects ? corrects.scheduledOn : slot?.scheduledOn ?? "",
      disposition,
      amountGiven: taken ? amount : "",
      givenAtUtc: time ? new Date(easternToUtcMs(day, time)).toISOString() : "",
      administeredByEmail: taken ? by : "",
      reason,
      reasonText,
      prnReportedBy: reportedBy,
      ...(isLate ? { lateReason: late } : {}),
      ...(counted ? { countBefore: before, countAfter: after, verifier: { email: vEmail, pin: vPin } } : {}),
      fromPaper: paper,
      notes,
      ...(corrects ? { correctsAdminKey: corrects.adminKey, correctionReason: why } : {}),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.data.error ?? "That didn't save.");
      return;
    }
    setVPin("");
    onSaved();
  }

  return (
    <div className="rounded-md border border-slate-300 bg-white p-3">
      {corrects && (
        <div className="mb-2">
          <label htmlFor={id("why")} className="field-label">Why this entry is being corrected (the original stays in the history) <span className="text-red-600">*</span></label>
          <input id={id("why")} className="field-input" required aria-required="true" {...bad("why")} value={why} onChange={(e) => setWhy(e.target.value)} />
          <P k="why" />
        </div>
      )}
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What happened">
        {choices.map((c) => (
          <button key={c} type="button" role="radio" aria-checked={disposition === c} className={`min-h-11 rounded-md border px-3 text-sm font-medium ${disposition === c ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => setDisposition(c)}>
            {DISPOSITION_LABEL[c]}
          </button>
        ))}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {taken && (
          <div>
            <label htmlFor={id("amt")} className="field-label">Amount given</label>
            <input id={id("amt")} className="field-input" {...bad("amt")} value={amount} onChange={(e) => setAmount(e.target.value)} />
            <P k="amt" />
          </div>
        )}
        <div>
          <label htmlFor={id("t")} className="field-label">{paper ? "Time on the paper (required)" : disposition === "PrnNotNeeded" ? "When reported" : "Time"}</label>
          <input id={id("t")} type="time" className="field-input" {...bad("t")} value={time} onChange={(e) => setTime(e.target.value)} />
          <P k="t" />
        </div>
        {taken && (
          <div>
            <label htmlFor={id("by")} className="field-label">{disposition === "SelfAdministered" ? "Supervised by" : "Administered by"}</label>
            <select id={id("by")} className="field-input" {...bad("by")} value={by} onChange={(e) => setBy(e.target.value)}>
              <option value="">Choose…</option>
              {o.givers.map((g) => <option key={g.email} value={g.email}>{g.name || g.email}{g.credential ? `, ${g.credential}` : ""}</option>)}
              {corrects && by === corrects.administeredByEmail && !o.givers.some((g) => g.email === by) && <option value={by}>{corrects.administeredByName || by}</option>}
            </select>
            <P k="by" />
            {by && by !== o.me.email && <p className="field-hint">Entered by you: {by} countersigns it with their own sign-in.</p>}
          </div>
        )}
        {(disposition === "Held" || disposition === "Refused") && (
          <>
            <div>
              <label htmlFor={id("r")} className="field-label">Reason (required)</label>
              {o.reasons.length ? (
                <select id={id("r")} className="field-input" {...bad("r")} value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="">Choose…</option>
                  {o.reasons.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              ) : (
                <p className="text-xs text-amber-900">No reasons set in Config: write the reason.</p>
              )}
              <P k="r" />
            </div>
            <div>
              <label htmlFor={id("rt")} className="field-label">Reason details{o.reasons.length ? " (optional)" : " (required)"}</label>
              <input id={id("rt")} className="field-input" {...bad("rt")} value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
              <P k="rt" />
            </div>
          </>
        )}
        {isLate && (
          <div>
            <label htmlFor={id("late")} className="field-label">Given after the window: why (required)</label>
            <input id={id("late")} className="field-input" {...bad("late")} value={late} onChange={(e) => setLate(e.target.value)} />
            <P k="late" />
          </div>
        )}
        {disposition === "PrnNotNeeded" && (
          <div>
            <label htmlFor={id("rep")} className="field-label">Who reported it isn't needed <span className="text-red-600">*</span></label>
            <input id={id("rep")} className="field-input" required aria-required="true" {...bad("rep")} value={reportedBy} onChange={(e) => setReportedBy(e.target.value)} placeholder="Student, or counselor's name" />
            <P k="rep" />
          </div>
        )}
      </div>
      {counted && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-950">Controlled: count before and after, and a second person signs with their own email and PIN on this device.</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div><label htmlFor={id("cb")} className="field-label">Count before</label><input id={id("cb")} className="field-input" {...bad("cb")} inputMode="numeric" value={before} onChange={(e) => setBefore(e.target.value.replace(/\D/g, ""))} /><P k="cb" /></div>
            <div><label htmlFor={id("ca")} className="field-label">Count after</label><input id={id("ca")} className="field-input" {...bad("ca")} inputMode="numeric" value={after} onChange={(e) => setAfter(e.target.value.replace(/\D/g, ""))} /><P k="ca" /></div>
            <div><label htmlFor={id("ve")} className="field-label">Second person's email</label><input id={id("ve")} type="email" autoComplete="off" className="field-input" {...bad("ve")} value={vEmail} onChange={(e) => setVEmail(e.target.value)} /><P k="ve" /></div>
            <div><label htmlFor={id("vp")} className="field-label">Their PIN</label><input id={id("vp")} type="password" inputMode="numeric" autoComplete="off" maxLength={4} className="field-input" {...bad("vp")} value={vPin} onChange={(e) => setVPin(e.target.value.replace(/\D/g, ""))} /><P k="vp" /></div>
          </div>
        </div>
      )}
      <div className="mt-3">
        <label htmlFor={id("n")} className="field-label">Note (optional)</label>
        <input id={id("n")} className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="btn-primary min-h-11" disabled={busy || !o.canRecord} onClick={save}>{corrects ? "Save the correction" : `Record: ${DISPOSITION_LABEL[disposition]}`}</button>
        {onCancel && <button type="button" className="btn-secondary min-h-11" onClick={onCancel}>Cancel</button>}
      </div>
      <p className="mt-2 text-xs text-slate-500">Program standard (MAR): student, date of birth, medication, dose, route and time as ordered are filled in; this records the amount, the time and who gave it.</p>
    </div>
  );
}

/** A scheduled slot: what's recorded (with Correct), or the controls. */
export function SlotCard({ personKey, slot, o, date, paper, onSaved, showWindow }: { personKey: string; slot: DoseSlot; o: DoseOptions; date: string; paper: boolean; onSaved: () => void; showWindow?: boolean }): React.ReactElement {
  const [correcting, setCorrecting] = useState(false);
  const [open, setOpen] = useState(false);
  const m = slot.med;
  return (
    <li className="rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0 break-words font-medium">{m.name} {m.strength} · {m.dose} · {m.route}{m.controlled ? " · controlled" : ""}{m.selfCarry ? " · self-carry" : ""}</p>
        {showWindow && <span className="text-sm text-slate-600">{windowName(o, slot.windowCode)}</span>}
      </div>
      <p className="text-xs text-slate-500">As ordered: {m.frequency}</p>
      <div className="mt-2">
        {slot.recorded && !correcting ? (
          <>
            <Recorded a={slot.recorded} history={slot.history} o={o} />
            {o.canRecord && <button type="button" className="mt-1 text-sm text-program underline" onClick={() => setCorrecting(true)}>Correct this entry</button>}
          </>
        ) : slot.recorded && correcting ? (
          <DoseForm personKey={personKey} med={m} slot={slot} date={date} o={o} paper={paper} corrects={slot.recorded} onSaved={() => { setCorrecting(false); onSaved(); }} onCancel={() => setCorrecting(false)} />
        ) : !o.canRecord ? (
          <p className="text-sm text-slate-600">Not recorded yet.</p>
        ) : open ? (
          <DoseForm personKey={personKey} med={m} slot={slot} date={date} o={o} paper={paper} onSaved={() => { setOpen(false); onSaved(); }} onCancel={() => setOpen(false)} />
        ) : (
          <button type="button" className="btn-primary min-h-11" onClick={() => setOpen(true)}>Record this dose</button>
        )}
      </div>
    </li>
  );
}

/** An as-needed medication: what was recorded today (each with Correct), and Record. */
export function PrnCard({ personKey, item, o, date, paper, onSaved, windowCode }: { personKey: string; item: StudentDoses["prn"][number]; o: DoseOptions; date: string; paper: boolean; onSaved: () => void; windowCode?: string }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const m = item.med;
  return (
    <li className="rounded-md border border-sky-200 bg-sky-50/40 p-3">
      <p className="font-medium">{m.name} {m.strength} · {m.dose} · {m.route}{m.controlled ? " · controlled" : ""}</p>
      <p className="text-sm">As needed: {m.prnInstructions || "as directed"}</p>
      {item.today.length > 0 && (
        <ul className="mt-2 space-y-1">
          {item.today.map((a) =>
            correcting === a.adminKey ? (
              <li key={a.adminKey}>
                <DoseForm personKey={personKey} med={m} date={date} o={o} paper={paper} corrects={a} onSaved={() => { setCorrecting(null); onSaved(); }} onCancel={() => setCorrecting(null)} />
              </li>
            ) : (
              <li key={a.adminKey}>
                <Recorded a={a} history={item.history?.[a.adminKey] ?? []} o={o} />
                {o.canRecord && <button type="button" className="mt-1 text-sm text-program underline" onClick={() => setCorrecting(a.adminKey)}>Correct this entry</button>}
              </li>
            )
          )}
        </ul>
      )}
      {o.canRecord && (open ? (
        <div className="mt-2"><DoseForm personKey={personKey} med={m} slot={windowCode ? { windowCode, scheduledOn: date } : undefined} date={date} o={o} paper={paper} onSaved={() => { setOpen(false); onSaved(); }} onCancel={() => setOpen(false)} /></div>
      ) : (
        <button type="button" className="btn-secondary mt-2 min-h-11" onClick={() => setOpen(true)}>Record (given / not needed)</button>
      ))}
    </li>
  );
}

function HeldBadges({ held }: { held: StudentDoses["held"] }) {
  if (!held.length) return null;
  return (
    <p className="mt-1 flex flex-wrap items-center gap-1 text-xs">
      {held.map((h) => <span key={h.medKey} className="flex items-center gap-1">{h.name} <MedStatusBadge status={h.status} /></span>)}
      <span className="text-slate-600">not given until authorized</span>
    </p>
  );
}

// ---- the pass -----------------------------------------------------------------------------------

const CITY_KEY = "medadmin.passCity";
function lastCity(): string | null {
  try {
    return localStorage.getItem(CITY_KEY);
  } catch {
    return null;
  }
}
function rememberCity(c: string) {
  try {
    localStorage.setItem(CITY_KEY, c);
  } catch {
    /* not remembered: fine */
  }
}

function StudentRow({ s, v, paper, reload }: { s: StudentDoses; v: PassView; paper: boolean; reload: () => void }) {
  const [open, setOpen] = useState(false);
  const pending = s.due.filter((d) => !d.recorded).length;
  return (
    <li className="rounded-md border border-slate-200 bg-white">
      <button type="button" className="flex min-h-14 w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="min-w-0">
          <span className="text-base font-semibold">{s.lastName}, {s.firstName}</span>
          {medicationAllergiesOf(s.allergy).length > 0 && <span className="ml-2"><MedicationAllergyTag /></span>}
          {s.allergy.allergies.some((a) => !/^medication/i.test(a.category)) && <span className="ml-2 rounded bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white">ALLERGY</span>}
          <span className="block text-xs text-slate-500">{s.personKey}</span>
        </span>
        <span className="text-sm">
          {s.prnOnly ? <span className="rounded bg-sky-100 px-2 py-1 font-bold text-sky-900">AS NEEDED — ask</span> : s.due.length ? (pending ? <span className="font-semibold text-amber-900">{pending} to give</span> : <span className="text-emerald-800">done</span>) : null}
          {s.held.length > 0 && <span className="ml-2 text-xs text-red-800">{s.held.length} held</span>}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-3">
          <AllergyBanner data={s.allergy} />
          <HeldBadges held={s.held} />
          {s.due.length > 0 && <ul className="mt-2 space-y-2">{s.due.map((d) => <SlotCard key={`${d.med.medKey}-${d.windowCode}`} personKey={s.personKey} slot={d} o={v} date={v.date} paper={paper} onSaved={reload} />)}</ul>}
          {s.prn.length > 0 && (
            <>
              <p className="mt-3 text-sm font-bold text-sky-900">AS NEEDED — ask</p>
              <ul className="mt-1 space-y-2">{s.prn.map((p) => <PrnCard key={p.med.medKey} personKey={s.personKey} item={p} o={v} date={v.date} paper={paper} onSaved={reload} windowCode={v.window?.code} />)}</ul>
            </>
          )}
          <p className="mt-2 text-sm"><a className="text-program underline" href={`/pass/student/${encodeURIComponent(s.personKey)}`}>All of today's doses for {s.firstName}</a></p>
        </div>
      )}
    </li>
  );
}

function PassScreen({ me }: { me: Me }) {
  const [paper, setPaper] = usePaperMode();
  const [windowCode, setWindowCode] = useState("");
  const [city, setCity] = useState<string | null>(lastCity());
  const [date, setDate] = useState("");
  const [v, setV] = useState<PassView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => {
    const q = new URLSearchParams({ window: windowCode, city: city ?? "", date });
    void api<{ pass: PassView }>("GET", `/api/pass?${q}`).then((r) => {
      if (!r.ok) return setError(r.data.error ?? "Could not load the pass.");
      setError(null);
      setV(r.data.pass);
      if (!windowCode && r.data.pass.window) setWindowCode(r.data.pass.window.code);
      if (city === null && r.data.pass.cities.length) setCity(r.data.pass.cities[0]);
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [windowCode, city, date]);
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Medication pass</h2>
      <p className="mb-3 text-sm text-slate-600">Pick the window and the city. Students with a dose due, alphabetical; tap one to give. Held or pending medications are never due. <a className="text-program underline" href="/missed">Missed meds</a> · <a className="text-program underline" href="/countersign">To countersign</a> · <a className="text-program underline" href="/print">Print center</a></p>
      <PaperBanner on={paper} set={setPaper} />
      <div className="section-card flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="ps-w" className="field-label">Window</label>
          <select id="ps-w" className="field-input min-h-11" value={windowCode} onChange={(e) => setWindowCode(e.target.value)}>
            {v?.windows.map((w) => <option key={w.code} value={w.code}>{w.name} ({w.startTime}–{w.endTime})</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ps-c" className="field-label">City</label>
          <select id="ps-c" className="field-input min-h-11" value={city ?? ""} onChange={(e) => { setCity(e.target.value); rememberCity(e.target.value); }}>
            {v?.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="(not assigned)">Not assigned</option>
            <option value="">All cities</option>
          </select>
        </div>
        <div>
          <label htmlFor="ps-d" className="field-label">Day</label>
          <input id="ps-d" type="date" className="field-input min-h-11" value={date || v?.date || ""} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {v && v.windows.length === 0 && <p className="section-card text-sm text-amber-900">No medication windows are set (Config: Medication windows).</p>}
      {v && v.window && (
        <div className="section-card">
          <p className="mb-2 text-sm text-slate-600">{v.window.name} ({v.window.startTime}–{v.window.endTime}) · {v.city || "All cities"} · {v.date} · {v.students.length} student{v.students.length === 1 ? "" : "s"}{!v.me.mayGive && " · you may record, but not be the one who gives a prescription dose"}</p>
          {v.students.length === 0 ? <p className="text-sm text-slate-600">No one has a dose due in this window.</p> : <ul className="space-y-2">{v.students.map((s) => <StudentRow key={s.personKey} s={s} v={v} paper={paper} reload={load} />)}</ul>}
        </div>
      )}
      <p className="sr-only">{me.name}</p>
    </section>
  );
}

// ---- the single-student screen ("Give now") ------------------------------------------------------------

function StudentScreen({ personKey }: { personKey: string }) {
  const [paper, setPaper] = usePaperMode();
  const [date, setDate] = useState("");
  const [v, setV] = useState<(DoseOptions & { date: string; student: StudentDoses }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    void api<{ view: DoseOptions & { date: string; student: StudentDoses } }>("GET", `/api/pass/student/${encodeURIComponent(personKey)}${date ? `?date=${date}` : ""}`).then((r) => (r.ok ? (setV(r.data.view), setError(null)) : setError(r.data.error ?? "Could not load.")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [personKey, date]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!v) return <p className="text-sm text-slate-600">Loading…</p>;
  const s = v.student;
  return (
    <div>
      <p className="mb-3 text-sm"><a href={`/station/${encodeURIComponent(personKey)}`} className="text-program underline">← Med station visit</a> · <a href="/pass" className="text-program underline">Medication pass</a></p>
      <PaperBanner on={paper} set={setPaper} />
      <AllergyBanner data={s.allergy} />
      <section className="section-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-semibold">Give now: {s.lastName}, {s.firstName}</h2>
          <span className="text-sm text-slate-600">{s.personKey} · born {s.dateOfBirth} · {s.city || "Not assigned"}</span>
        </div>
        <div className="mt-2">
          <label htmlFor="ss-d" className="field-label">Day</label>
          <input id="ss-d" type="date" className="field-input max-w-[12rem]" value={date || v.date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <HeldBadges held={s.held} />
      </section>
      <section className="section-card">
        <h3 className="mb-2 text-base font-semibold">Scheduled doses, every window</h3>
        {s.due.length === 0 ? <p className="text-sm text-slate-600">No scheduled medications to give.</p> : <ul className="space-y-2">{s.due.map((d) => <SlotCard key={`${d.med.medKey}-${d.windowCode}`} personKey={s.personKey} slot={d} o={v} date={v.date} paper={paper} onSaved={load} showWindow />)}</ul>}
      </section>
      {s.prn.length > 0 && (
        <section className="section-card">
          <h3 className="mb-2 text-base font-semibold">As needed</h3>
          <ul className="space-y-2">{s.prn.map((p) => <PrnCard key={p.med.medKey} personKey={s.personKey} item={p} o={v} date={v.date} paper={paper} onSaved={load} />)}</ul>
        </section>
      )}
    </div>
  );
}

export function Pass({ path, me }: { path: string; me: Me }): React.ReactElement {
  const key = path.startsWith("/pass/student/") ? decodeURIComponent(path.slice("/pass/student/".length)) : null;
  return key ? <StudentScreen key={key} personKey={key} /> : <PassScreen me={me} />;
}
